/**
 * NotificationPromptDrawer
 *
 * A bottom drawer that appears after habit creation to ask users
 * to enable push notifications. Works on both native (iOS/Android)
 * and desktop/web (browser Notification API).
 *
 * When the user taps "Enable Notifications", the native OS or
 * browser permission dialog is triggered.
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { FluentEmoji } from '@/components/ui/fluent-emoji'
import {
  isNativePlatform,
  requestPushPermissions,
} from '@/lib/native'
import { supabase } from '@/lib/supabase'
import { track, trackFunnel } from '@/lib/analytics'

const DISMISSED_KEY = 'miac:notification-prompt-dismissed'

interface NotificationPromptDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NotificationPromptDrawer({
  open,
  onOpenChange,
}: NotificationPromptDrawerProps) {
  const [isRequesting, setIsRequesting] = useState(false)

  // Track funnel when prompt is shown
  useEffect(() => {
    if (open) trackFunnel('notification_optin', 'prompt_shown', 0)
  }, [open])

  const dismiss = () => {
    track('notification_prompt_declined', {}, 'notification')
    onOpenChange(false)
  }

  const neverShowAgain = () => {
    track('notification_prompt_declined', {}, 'notification')
    localStorage.setItem(DISMISSED_KEY, 'true')
    onOpenChange(false)
  }

  const handleEnable = async () => {
    track('notification_prompt_accepted', {}, 'notification')
    trackFunnel('notification_optin', 'prompt_accepted', 1)
    setIsRequesting(true)

    try {
      let granted = false

      if (isNativePlatform()) {
        granted = await requestPushPermissions()
      } else if ('Notification' in window) {
        const permission = await Notification.requestPermission()
        granted = permission === 'granted'
      }

      if (granted) {
        // Persist the preference in the database
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (user) {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
          await supabase
            .from('user_profiles')
            .update({
              push_enabled: true,
              notification_timezone: timezone,
            })
            .eq('id', user.id)
        }

        // Mark as permanently handled so we don't show again
        localStorage.setItem(DISMISSED_KEY, 'true')
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error)
    } finally {
      setIsRequesting(false)
      onOpenChange(false)
    }
  }

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) dismiss() }}>
      <DrawerContent>
        <div className="mx-auto w-full max-w-md px-4 pb-2">
          <DrawerHeader className="text-center pb-2">
            {/* Animated bell illustration */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="flex justify-center mb-2"
            >
              <div className="relative">
                {/* Notification ring pulse */}
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: [0.8, 1.6, 0.8], opacity: [0.4, 0, 0.4] }}
                  transition={{ repeat: Infinity, duration: 2, delay: 0.5, ease: 'easeInOut' }}
                >
                  <div className="w-16 h-16 rounded-full bg-amber-400/20 dark:bg-amber-400/10" />
                </motion.div>
                <motion.div
                  animate={{ rotate: [0, 10, -10, 10, 0] }}
                  transition={{ delay: 0.6, duration: 0.6, ease: 'easeInOut' }}
                >
                  <FluentEmoji emoji="🔔" size={48} />
                </motion.div>
                <motion.div
                  className="absolute -bottom-1 -right-2"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.4, type: 'spring', stiffness: 400 }}
                >
                  <FluentEmoji emoji="✨" size={24} />
                </motion.div>
              </div>
            </motion.div>

            <DrawerTitle className="text-lg font-bold text-slate-900 dark:text-white">
              Never miss a habit
            </DrawerTitle>
            <DrawerDescription className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Get friendly reminders to keep your streak alive and stay on track.
            </DrawerDescription>
          </DrawerHeader>

          {/* Benefits card */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 overflow-hidden">
            {[
              { emoji: '⏰', text: 'Daily reminders at your preferred time' },
              { emoji: '🔥', text: 'Streak alerts so you never break your chain' },
              { emoji: '🎉', text: 'Milestone celebrations when you level up' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.1, duration: 0.3 }}
                className={`px-4 py-3 flex items-center gap-3 ${
                  i < 2 ? 'border-b border-slate-200 dark:border-slate-700' : ''
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                  <FluentEmoji emoji={item.emoji} size={18} />
                </div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>

          <p className="text-xs text-center text-slate-400 dark:text-slate-500 mt-3 px-2">
            You can adjust notification settings anytime in Settings.
          </p>

          {/* Actions */}
          <DrawerFooter className="flex flex-col gap-2 pt-3 pb-4">
            <Button
              onClick={handleEnable}
              disabled={isRequesting}
              className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium"
            >
              {isRequesting ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                />
              ) : (
                <span className="flex items-center gap-2">
                  <FluentEmoji emoji="🔔" size={18} />
                  Enable Notifications
                </span>
              )}
            </Button>

            {/* Tertiary row */}
            <div className="flex items-center justify-center gap-3 pt-1">
              <button
                onClick={dismiss}
                disabled={isRequesting}
                className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                Maybe later
              </button>
              <span className="text-slate-200 dark:text-slate-700">|</span>
              <button
                onClick={neverShowAgain}
                disabled={isRequesting}
                className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                Don&apos;t ask again
              </button>
            </div>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

/**
 * Check whether the prompt has been permanently dismissed by the user.
 */
export function isNotificationPromptDismissed(): boolean {
  return localStorage.getItem(DISMISSED_KEY) === 'true'
}
